import { MenuItem, Text, Tooltip } from "@chakra-ui/react";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { useSubStatusTransition } from "src/hooks/useSubStatusTransition";
import { maybePluralize } from "src/services/format";
import { FormSchoolStatus } from "../types";
import { useBulkActionTargets } from "./bulkActionTargetsContext";
import { trackFormAction } from "./trackFormAction";
import { useEnqueueBulkSubstatus } from "./useEnqueueBulkSubstatus";

interface Props {
  selectedFormSchoolStatuses: FormSchoolStatus[];
}

const CONFIRMATION_BODY_TEXT =
  "Clearing the sub-status will delete any offer or waitlist information, including acceptances, programs or waitlists positions. This can’t be undone nor recovered.";

const DISABLED_TOOLTIP_TEXT =
  "This form is associated with another, revoke the offer or remove from waitlist instead";

export const BulkClearSubstatus = ({ selectedFormSchoolStatuses }: Props) => {
  const bulk = useBulkActionTargets();
  const enqueueBulkSubstatus = useEnqueueBulkSubstatus();
  const { isSubStatusTransitionAllowed } = useSubStatusTransition();

  const { confirm, confirmationDialog, setBody, setHeader } =
    useConfirmationDialog({
      header: null,
      body: null,
      cancelButton: {
        label: "Cancel",
      },
      confirmButton: {
        label: "Yes, clear",
        colorScheme: "blue",
      },
      translate: true,
    });

  const handleOnClick = async () => {
    const count = bulk.selectionSize;
    trackFormAction({
      verb: "Clicked",
      actionType: "CLEAR_SUBSTATUS",
      bulk: true,
      count,
      async: true,
    });

    setHeader(
      <Text>{`Clear sub-status from ${count} ${maybePluralize(
        count,
        "item"
      )}?`}</Text>
    );
    setBody(<Text>{CONFIRMATION_BODY_TEXT}</Text>);
    if (!(await confirm())) {
      trackFormAction({
        verb: "Canceled",
        actionType: "CLEAR_SUBSTATUS",
        bulk: true,
        count,
        async: true,
      });
      return;
    }

    trackFormAction({
      verb: "Confirmed",
      actionType: "CLEAR_SUBSTATUS",
      bulk: true,
      count,
      async: true,
    });
    await enqueueBulkSubstatus({ action: "CLEAR_SUBSTATUS" });
  };

  const hasAssignedForms = selectedFormSchoolStatuses.some(
    (result) =>
      (result.offers?.[0] && result.offers?.[0].assigned_forms.length > 0) ||
      (result.waitlists?.[0] && result.waitlists?.[0].assigned_forms.length > 0)
  );
  const canClear =
    !hasAssignedForms &&
    selectedFormSchoolStatuses.every((result) =>
      isSubStatusTransitionAllowed(result.sub_status, null)
    );

  return (
    <>
      <Tooltip
        label={<Text align="center">{DISABLED_TOOLTIP_TEXT}</Text>}
        placement="left"
        hasArrow
        arrowSize={15}
        gutter={20}
        minW="22rem"
        isDisabled={!hasAssignedForms}
      >
        <MenuItem
          textColor="gray.700"
          fontWeight="400"
          fontSize="sm"
          onClick={handleOnClick}
          isDisabled={!canClear}
        >
          Clear sub-status
        </MenuItem>
      </Tooltip>
      {confirmationDialog}
    </>
  );
};
